import GeoJSON from 'geojson'

import { Navaid, NavaidPurpose, NavaidType } from '@/domain/models/Waypoint/Navaid'
import { Coordinates } from '@/domain/protocols/Coordinates'

import { NavaidIconId } from './icons'

import { convertDecimalDegreesToDMS } from '@/utils/coordinates'

export type NavaidIconSlug = Extract<NavaidIconId, 'vor' | 'ndb' | 'vor-dme' | 'dme'>

export interface NavaidFeatureInfoData {
  id: string
  code: string
  name?: string
  navaidType: NavaidType
  frequency: number
  coordinates: Coordinates
  // Campos do FlatRecord (opcionais — somem do painel quando ausentes).
  frequencyUom?: string
  purpose?: NavaidPurpose
  operationalHours?: string
  remarks?: string
}

// Reconstrói os dados completos do painel a partir do modelo Navaid. Usado tanto no clique
// do auxílio solto no mapa quanto no clique de um auxílio já adicionado à rota — em ambos os
// casos o painel exibe todas as seções (purpose/operationalHours/remarks/frequencyUom).
export function makeNavaidFeatureInfoData(navaid: Navaid): NavaidFeatureInfoData {
  return {
    id: navaid.id.toString(),
    code: navaid.code,
    name: navaid.name || undefined,
    navaidType: navaid.extra.type,
    frequency: navaid.extra.frequency,
    coordinates: navaid.coordinates,
    frequencyUom: navaid.extra.frequencyUom,
    purpose: navaid.extra.purpose,
    operationalHours: navaid.extra.operationalHours,
    remarks: navaid.extra.remarks
  }
}

const NAVAID_TYPE_TO_ICON_SLUG: Record<NavaidType, NavaidIconSlug> = {
  VOR: 'vor',
  NDB: 'ndb',
  'VOR/DME': 'vor-dme',
  DME: 'dme'
}

const NAVAID_TYPES = Object.keys(NAVAID_TYPE_TO_ICON_SLUG) as NavaidType[]

function isNavaidType(value: unknown): value is NavaidType {
  return typeof value === 'string' && NAVAID_TYPES.includes(value as NavaidType)
}

export function mapNavaidTypeToIconSlug(type: NavaidType | string | undefined): NavaidIconSlug {
  if (isNavaidType(type)) return NAVAID_TYPE_TO_ICON_SLUG[type]
  return 'vor'
}

// Prioridade da label na detecção de colisão do Mapbox: quando duas labels de navaids
// se sobrepõem, vence a de MENOR valor (é colocada primeiro via `symbol-sort-key`).
// Ordem desejada: VOR/DME > VOR > DME > NDB.
const NAVAID_LABEL_PRIORITY: Record<NavaidType, number> = {
  'VOR/DME': 1,
  VOR: 2,
  DME: 3,
  NDB: 4
}

export function getNavaidLabelPriority(type: NavaidType | string | undefined): number {
  if (isNavaidType(type)) return NAVAID_LABEL_PRIORITY[type]
  return NAVAID_LABEL_PRIORITY.NDB // tipo desconhecido → menor prioridade
}

// Normaliza a unidade vinda do FlatRecord (ex.: 'MHZ' → 'MHz', 'KHZ' → 'kHz').
export function normalizeFrequencyUom(uom: string): string {
  const normalized = uom.trim().toUpperCase()
  if (normalized === 'MHZ') return 'MHz'
  if (normalized === 'KHZ') return 'kHz'
  return uom.trim()
}

// Resolve a unidade da frequência: usa a do dado quando disponível; senão deriva do tipo
// (NDB → kHz, demais → MHz).
function resolveFrequencyUom(type: NavaidType | string | undefined, frequencyUom?: string): string {
  return frequencyUom ? normalizeFrequencyUom(frequencyUom) : type === 'NDB' ? 'kHz' : 'MHz'
}

// Formata apenas o valor numérico da frequência, sem unidade (NDB → inteiro; demais → até 2 casas).
// Para MHz, evita zeros desnecessários: mostra 1 casa quando a 2ª for 0 (112.3, 115.0) e mantém 2
// casas caso contrário (112.35). Usado nas labels do mapa, onde a unidade é omitida.
export function formatNavaidFrequencyValue(
  frequency: number,
  type: NavaidType | string | undefined,
  frequencyUom?: string
): string {
  if (!Number.isFinite(frequency)) return ''
  const isKHz = resolveFrequencyUom(type, frequencyUom).toLowerCase() === 'khz'
  return isKHz ? `${Math.round(frequency)}` : frequency.toFixed(2).replace(/0$/, '')
}

// Formata a frequência com unidade (ex.: '113.70 MHz', '340 kHz'). Usado no painel de informações.
export function formatNavaidFrequency(
  frequency: number,
  type: NavaidType | string | undefined,
  frequencyUom?: string
): string {
  const value = formatNavaidFrequencyValue(frequency, type, frequencyUom)
  if (!value) return ''
  return `${value} ${resolveFrequencyUom(type, frequencyUom)}`
}

export function formatNavaidCoordinates(coordinates: Coordinates): string {
  return convertDecimalDegreesToDMS(coordinates.latitude, coordinates.longitude)
}

export function projectNavaidFeature(
  feature: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> | null | undefined
): NavaidFeatureInfoData | null {
  if (!feature || !feature.properties) return null

  const properties = feature.properties

  const id = properties.id
  if (typeof id !== 'string' || id.length === 0) return null

  const code = properties.code
  if (typeof code !== 'string' || code.length === 0) return null

  if (!isNavaidType(properties.type)) return null

  if (typeof properties.frequency !== 'number' || !Number.isFinite(properties.frequency)) return null

  if (!feature.geometry || feature.geometry.type !== 'Point') return null

  const [longitude, latitude] = feature.geometry.coordinates ?? []
  if (typeof longitude !== 'number' || typeof latitude !== 'number') return null
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null

  const name = typeof properties.name === 'string' && properties.name.length > 0 ? properties.name : undefined

  return {
    id,
    code,
    name,
    navaidType: properties.type,
    frequency: properties.frequency,
    coordinates: Coordinates.create({ latitude, longitude })
  }
}
